import type { FormEvent } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import ModalFooterActionContainer from '../TripModal/TripItems/Create/ModalFooterActionContainer'

import TextInput from 'components/Common/Form/Input/TextInput'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Caption from 'LuxKit/Typography/Caption'
import {
  useRenameAttachedDocument,
  useRenameUnattachedDocument,
} from 'tripPlanner/hooks/api/documents'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'

const MAX_FILENAME_CHARS = 100

interface Props {
  tripId: string
  document: TripDocumentWithItemInfo
}

function RenameDocumentModal({ tripId, document }: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal } = useModalCallbacks()
  const modalElement = useModalElementContext()
  const lastDotIndex = document.filename.lastIndexOf('.')
  const nameWithoutExt = lastDotIndex > 0 ?
      document.filename.substring(0, lastDotIndex) :
    document.filename
  const extension = lastDotIndex > 0 ?
      document.filename.substring(lastDotIndex) :
    ''
  const isAttached = !!document.item

  const [errorMsg, setErrorMsg] = useState('')
  const [nameLength, setNameLength] = useState(nameWithoutExt.length)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const close = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const renameSuccessMessage = intl.formatMessage({
    id: 'pdwJel6sZwswrAX327Wb',
    defaultMessage: 'Document renamed successfully',
    description: 'Success notification after renaming a trip document',
  })
  const renameErrorMessage = intl.formatMessage({
    id: 'zw-kEnDk2nywogC3szan',
    defaultMessage: 'Failed to rename document',
    description: 'Error notification when trip document rename fails',
  })

  const { mutate: renameAttachedDocument, isLoading: isRenamingAttached } =
    useRenameAttachedDocument({
      onSuccess: () => {
        showSnackbar(renameSuccessMessage, 'success')
        close()
      },
      onError: () => {
        showSnackbar(renameErrorMessage, 'critical')
      },
      tripId,
    })

  const { mutate: renameUnattachedDocument, isLoading: isRenamingUnattached } =
    useRenameUnattachedDocument({
      onSuccess: () => {
        showSnackbar(renameSuccessMessage, 'success')
        close()
      },
      onError: () => {
        showSnackbar(renameErrorMessage, 'critical')
      },
      tripId,
    })

  const isLoading = isRenamingAttached || isRenamingUnattached

  const handleRename = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const formData = formToObject<{ newName: string }>(e.currentTarget)
      const trimmedName = formData.newName.trim()

      if (!trimmedName) {
        setErrorMsg(intl.formatMessage({
          id: 'zdxFRrPweYRhmMlExFT_',
          defaultMessage: 'Filename cannot be empty',
          description: 'Validation error when user tries to rename a document with an empty name',
        }))
        return
      }

      if (trimmedName === nameWithoutExt) {
        close()
        return
      }

      const newFileName = trimmedName + extension

      if (isAttached && document.item) {
        renameAttachedDocument({
          tripId,
          tripItemId: document.item.id,
          fileName: document.filename,
          newFileName,
        })
      } else {
        renameUnattachedDocument({
          tripId,
          fileName: document.filename,
          newFileName,
        })
      }
    },
    [
      intl,
      nameWithoutExt,
      extension,
      isAttached,
      document,
      tripId,
      close,
      renameAttachedDocument,
      renameUnattachedDocument,
    ],
  )

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg('')
    setNameLength(e.currentTarget.value.length)
  }, [])

  return (
    <ModalBase>
      <form onSubmit={handleRename}>
        <ModalHeader
          title={intl.formatMessage({
            id: '4WikbVa_CnGDqnzOxE7Z',
            defaultMessage: 'Rename your document',
            description: 'Modal title for the document rename dialog dialog window',
          })}
          onCloseButtonClick={close}
        />
        <ModalBody>
          <ModalContent>
            <TextInput
              ref={inputRef}
              name="newName"
              label={intl.formatMessage({
                id: '2g_rJqfRr8G3yR8K8OkG',
                defaultMessage: 'Document name',
                description: 'Input label for the document name field in rename modal',
              })}
              placeholder={intl.formatMessage({
                id: 'mnQcpfvkXMjqJjIE_6P8',
                defaultMessage: 'Enter new document name',
                description: 'Placeholder text for the document name input in rename modal',
              })}
              defaultValue={nameWithoutExt}
              onChange={onInputChange}
              maxLength={MAX_FILENAME_CHARS}
              invalidErrorMessage={errorMsg}
              manualError={!!errorMsg}
              disabled={isLoading}
              required
              width="100%"
            />
            <Caption variant="medium" colour="neutral-three" align="end">
              {intl.formatMessage(
                {
                  id: 'AeAL6MeENr4AsXYVGiEi',
                  defaultMessage: '{currentLength}/{maxLength}',
                  description: 'Character count indicator showing current length vs maximum allowed for document name',
                },
                { currentLength: nameLength, maxLength: MAX_FILENAME_CHARS },
              )}
            </Caption>
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <ModalFooterActionContainer>
            <TextButton
              kind="tertiary"
              size="large"
              fit="mobile-full-width"
              onClick={close}
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: 'TRij0kxCsM3SXvE95fBo',
                defaultMessage: 'Cancel',
                description: 'Button to cancel document rename and close the modal',
              })}
            </TextButton>
            <TextButton
              kind="primary"
              size="large"
              fit="mobile-full-width"
              type="submit"
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: 'TBMc6zr0PRCzaxsEWb0r',
                defaultMessage: 'Confirm',
                description: 'Action button to confirm renaming the document in the trip planner documents section',
              })}
            </TextButton>
          </ModalFooterActionContainer>
        </ModalFooter>
      </form>
    </ModalBase>
  )
}

export default RenameDocumentModal
